// @vendors
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { map as _map } from 'lodash';

// @state
import { useUI } from '../../../app/context/ui.js';

// @material-ui
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '../../../components/shared/MaterialUI.js';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';

// @helpers
import AppHelper from '../../../helpers/AppHelper.js';
import { formatSSN, getRawSSN } from '../../../helpers/FormatSSN.js';
import { formatPhoneNumber } from '../../../helpers/FormatPhoneNumber.js';

// @services
import QuestionnaireServiceNewApi from '../../../services/newApi/QuestionnaireServiceNewApi.js';
import HelpersServiceNewApi from '../../../services/newApi/HelpersServiceNewApi.js';

// @components
import { QuestionnaireContext } from '../Prescreening.js';

// @assets
import { theme } from '../../../assets/theme.js';

const FIELD_CONFIG = [
  { key: 'first_name', label: 'First Name', labelEs: 'Nombre', type: 'text' },
  { key: 'middle_name', label: 'Middle Name', labelEs: 'Segundo nombre', type: 'text', optional: true },
  { key: 'last_name', label: 'Last Name', labelEs: 'Apellido', type: 'text' },
  { key: 'date_of_birth', label: 'Date of Birth', labelEs: 'Fecha de nacimiento', type: 'date' },
  { key: 'social_number', label: 'SSN', labelEs: 'SSN', type: 'ssn' },
  { key: 'address', label: 'Address', labelEs: 'Direccion', type: 'text' },
  { key: 'state', label: 'State', labelEs: 'Estado', type: 'select_state' },
  { key: 'county', label: 'County', labelEs: 'Condado', type: 'select_county' },
  { key: 'city', label: 'City', labelEs: 'Ciudad', type: 'select_city' },
  { key: 'zip_code', label: 'Zip Code', labelEs: 'Codigo postal', type: 'zip' },
  { key: 'phone_number', label: 'Phone', labelEs: 'Telefono', type: 'phone' },
  { key: 'email', label: 'Email', labelEs: 'Correo electronico', type: 'text' },
];

const DemographicReview = () => {
  const {
    hashToken,
    lang,
    demographicData,
    setDemographicData,
    setShowDemoReview,
  } = useContext(QuestionnaireContext);

  const { blockUI, snackbarUI } = useUI();
  const questionnaireService = useMemo(() => new QuestionnaireServiceNewApi(), []);
  const helpersService = useMemo(() => new HelpersServiceNewApi(), []);
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [isOtherCounty, setIsOtherCounty] = useState(false);

  // Dropdown options
  const [stateOptions, setStateOptions] = useState([]);
  const [countyOptions, setCountyOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);

  const data = demographicData || {};

  // Load states on mount
  useEffect(() => {
    loadStates();
    if (data.is_manual) {
      setIsOtherCounty(true);
    }
  }, []);

  // Load counties when state exists
  useEffect(() => {
    if (data.state && !isOtherCounty) {
      loadCounties(data.state);
    }
  }, [data.state]);

  // Load cities when county exists
  useEffect(() => {
    if (data.state && data.county && !isOtherCounty) {
      loadCities(data.state, data.county);
    }
  }, [data.county]);

  const loadStates = async () => {
    try {
      const resp = await helpersService.getLocalities();
      const states = resp?.data?.data || [];
      setStateOptions(_map(states, (s) => ({ id: s.name, name: s.name })));
    } catch (e) {}
  };

  const loadCounties = async (state) => {
    try {
      const resp = await helpersService.getLocalities(state);
      const counties = resp?.data?.data || [];
      setCountyOptions(_map(counties, (c) => ({ id: c.name, name: c.name })));
    } catch (e) {}
  };

  const loadCities = async (state, county) => {
    try {
      const resp = await helpersService.getLocalities(state, county);
      const cities = resp?.data?.data || [];
      setCityOptions(_map(cities, (c) => ({ id: c.name, name: c.name })));
    } catch (e) {}
  };

  const handleEdit = (fieldKey, currentValue) => {
    setEditingField(fieldKey);
    // Convert date format for date input (MM/DD/YYYY or other → YYYY-MM-DD)
    if (fieldKey === 'date_of_birth' && currentValue) {
      const parts = currentValue.split('/');
      if (parts.length === 3) {
        setEditValue(`${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`);
        return;
      }
    }
    setEditValue(currentValue || '');
  };

  const handleCancel = () => {
    setEditingField(null);
    setEditValue('');
  };

  const handleSave = (fieldKey) => {
    let saveValue = editValue;
    // Convert date back from YYYY-MM-DD to MM/DD/YYYY
    if (fieldKey === 'date_of_birth' && editValue && editValue.includes('-')) {
      const [y, m, d] = editValue.split('-');
      saveValue = `${m}/${d}/${y}`;
    }
    const updatedData = { ...data, [fieldKey]: saveValue };

    // Cascade: if state changed, clear county and city
    if (fieldKey === 'state') {
      updatedData.county = '';
      updatedData.city = '';
      setCountyOptions([]);
      setCityOptions([]);
      loadCounties(editValue);
    }
    // If county changed, clear city
    if (fieldKey === 'county') {
      updatedData.city = '';
      setCityOptions([]);
      if (data.state) loadCities(data.state, editValue);
    }

    setDemographicData(updatedData);
    setEditingField(null);
    setEditValue('');
  };

  const handleCheckboxChange = (e) => {
    const checked = e.target.checked;
    setIsOtherCounty(checked);
    if (checked) {
      setDemographicData({ ...data, county: '', city: '', is_manual: true });
    } else {
      setDemographicData({ ...data, county: '', city: '', is_manual: false });
      if (data.state) loadCounties(data.state);
    }
  };

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const validateRequired = () => {
    const requiredFields = FIELD_CONFIG.filter((f) => !f.optional);
    const missing = requiredFields.filter((f) => !data[f.key]);
    return missing;
  };

  const [validationError, setValidationError] = useState('');

  const handleContinueToSignature = async () => {
    const missing = validateRequired();
    if (missing.length > 0) {
      const labels = missing.map((f) => lang === 'es' ? f.labelEs : f.label).join(', ');
      setValidationError(
        lang === 'es'
          ? `Los siguientes campos son obligatorios: ${labels}`
          : `The following fields are required: ${labels}`
      );
      return;
    }
    if (data.email && !isValidEmail(data.email)) {
      setValidationError(
        lang === 'es' ? 'El correo electrónico no es válido' : 'Email address is not valid'
      );
      return;
    }
    setValidationError('');
    try {
      blockUI.current.open(true);
      const payload = {
        ...data,
        is_manual: isOtherCounty,
        opt_received: data.opt_received ?? false,
      };
      await questionnaireService.updateEmployeeDemographics(payload, hashToken);
      blockUI.current.open(false);
      setShowDemoReview(true);
    } catch (e) {
      blockUI.current.open(false);
      AppHelper.checkError(e, snackbarUI);
    }
  };

  const handleChangeEdit = (value, fieldType) => {
    if (fieldType === 'ssn') {
      const raw = getRawSSN(value);
      setEditValue(formatSSN(raw));
    } else if (fieldType === 'phone') {
      setEditValue(formatPhoneNumber(value));
    } else {
      setEditValue(value);
    }
  };

  const displayValue = (value) => {
    if (!value) return '-';
    return value;
  };

  const editBtnSx = {
    ml: 1,
    minWidth: '56px !important',
    width: 'auto !important',
    height: '28px !important',
    marginTop: '0 !important',
    flexShrink: 0,
    backgroundColor: '#4caf50 !important',
    color: '#fff !important',
    textTransform: 'none !important',
    fontWeight: 500,
    fontSize: '0.75rem !important',
    padding: '2px 12px !important',
    borderRadius: '4px',
    boxShadow: 'none !important',
    '&:hover': {
      backgroundColor: '#43a047 !important',
    },
  };

  const selectSx = {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
      },
    },
  };

  const getOptionsForField = (fieldType) => {
    if (fieldType === 'select_state') return stateOptions;
    if (fieldType === 'select_county') return countyOptions;
    if (fieldType === 'select_city') return cityOptions;
    return [];
  };

  const isSelectField = (fieldType) => {
    return ['select_state', 'select_county', 'select_city'].includes(fieldType);
  };

  const renderEditField = (field) => {
    // For select fields (state/county/city), show dropdown when not isOtherCounty
    if (isSelectField(field.type) && !isOtherCounty) {
      const options = getOptionsForField(field.type);
      return (
        <Select
          size="small"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          fullWidth
          displayEmpty
          sx={selectSx}
        >
          <MenuItem value="" disabled>-- Select --</MenuItem>
          {options.map((opt) => (
            <MenuItem key={opt.id} value={opt.name}>{opt.name}</MenuItem>
          ))}
        </Select>
      );
    }

    if (field.type === 'date') {
      return (
        <TextField
          size="small"
          type="date"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          autoFocus
          fullWidth
          InputLabelProps={{ shrink: true }}
          sx={{
            '& .MuiOutlinedInput-root': {
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
            },
          }}
        />
      );
    }

    if (field.type === 'zip') {
      return (
        <TextField
          size="small"
          value={editValue}
          onChange={(e) => {
            const val = e.target.value.replace(/\D/g, '').slice(0, 5);
            setEditValue(val);
          }}
          autoFocus
          fullWidth
          inputProps={{ maxLength: 5 }}
          sx={{
            '& .MuiOutlinedInput-root': {
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
            },
          }}
        />
      );
    }

    return (
      <TextField
        size="small"
        value={editValue}
        onChange={(e) => handleChangeEdit(e.target.value, field.type)}
        autoFocus
        fullWidth
        sx={{
          '& .MuiOutlinedInput-root': {
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main,
            },
          },
        }}
      />
    );
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography
        sx={{
          fontSize: '1.5rem',
          fontWeight: 700,
          color: theme.palette.primary.black,
          mb: 1,
          textAlign: 'center',
        }}
      >
        {lang === 'es' ? 'Informacion Personal:' : 'Personal Information:'}
      </Typography>

      <Typography
        sx={{
          fontSize: '1rem',
          color: theme.palette.secondary.black,
          mb: 3,
          textAlign: 'center',
        }}
      >
        {lang === 'es'
          ? 'Por favor revisa tu informacion antes de continuar. Si algo es incorrecto, haz clic en Editar para corregirlo.'
          : 'Please review your information before continuing. If anything is incorrect, click Edit to correct it.'}
      </Typography>

      <Box
        sx={{
          borderTop: `2px solid ${theme.palette.primary.main}`,
          pt: 2,
        }}
      >
        {FIELD_CONFIG.map((field) => {
          if (field.hidden) return null;
          const value = data[field.key];
          const isEditing = editingField === field.key;
          const fieldLabel = lang === 'es' ? field.labelEs : field.label;

          if (isEditing) {
            return (
              <Box
                key={field.key}
                sx={{
                  py: 1.5,
                  px: 2,
                  borderBottom: '1px solid rgba(224,224,224,0.6)',
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: '0.95rem', md: '1.125rem' },
                    color: theme.palette.primary.black,
                    mb: 1,
                  }}
                >
                  {fieldLabel}:
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {renderEditField(field)}
                  <IconButton
                    onClick={() => handleSave(field.key)}
                    sx={{ color: theme.palette.primary.main }}
                  >
                    <CheckIcon />
                  </IconButton>
                  <IconButton onClick={handleCancel} sx={{ color: theme.palette.error.main }}>
                    <CloseIcon />
                  </IconButton>
                </Box>
              </Box>
            );
          }

          // Render the checkbox after the state field
          const showCheckboxAfter = field.key === 'state';

          return (
            <React.Fragment key={field.key}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 1.5,
                  px: 2,
                  borderBottom: '1px solid rgba(224,224,224,0.6)',
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'baseline', gap: 0.5, flexShrink: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: '0.95rem', md: '1.125rem' },
                      color: theme.palette.primary.black,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {fieldLabel}: {!field.optional && <span style={{ color: 'red' }}>*</span>}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: '0.95rem', md: '1.125rem' },
                      color: theme.palette.secondary.black,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {displayValue(value)}
                  </Typography>
                </Box>

                <Button
                  variant="contained"
                  size="small"
                  onClick={() => handleEdit(field.key, value)}
                  sx={editBtnSx}
                >
                  {lang === 'es' ? 'Editar' : 'Edit'}
                </Button>
              </Box>

              {showCheckboxAfter && (
                <Box sx={{ px: 2, py: 1 }}>
                  <FormControlLabel
                    checked={isOtherCounty}
                    control={
                      <Checkbox
                        color="success"
                        onChange={handleCheckboxChange}
                      />
                    }
                    label={lang === 'es' ? 'Mi condado o ciudad no esta listado' : 'My county or city is not listed'}
                  />
                </Box>
              )}
            </React.Fragment>
          );
        })}
      </Box>

      {validationError && (
        <Typography sx={{ color: theme.palette.error.main, textAlign: 'center', mt: 2, fontSize: '0.875rem' }}>
          {validationError}
        </Typography>
      )}

      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <Button
          onClick={handleContinueToSignature}
          sx={{
            width: '100%',
            maxWidth: '400px',
            height: '52px',
            backgroundColor: theme.palette.primary.black,
            color: theme.palette.primary.white,
            textTransform: 'none',
            fontSize: '1.125rem',
            fontWeight: 600,
            '&:hover': {
              backgroundColor: theme.palette.secondary.black,
              boxShadow: 3,
            },
          }}
        >
          {lang === 'es' ? 'Continuar a Firma Digital' : 'Continue to Digital Signature'}
        </Button>
      </Box>
    </Box>
  );
};

export default DemographicReview;
